import { toast } from "react-toastify";
import {
  dataLocalStorage,
  getLocalStorage,
  isDevelopmentMode,
  printData,
} from "src/helper/publicFunction";
import { setLoading } from "src/store/customizer/CustomizerSlice";
import { SessionEnded } from "src/store/data/user/actionsUser";
import axios from "src/utils/axios";
import axios_client from "src/utils/axios_client";
import { createElement } from "react";
import UndoToast from "src/components/shared/UndoToast";
import { canUndo, undoFor } from "src/utils/undoDelete";

// What a person sees when a request fails: the server's own message when it
// is a plain sentence, otherwise a plain fallback. Never a stack, a status
// code, SQL or an empty red toast.
// Each failure says what kind it is and that nothing changed: the same
// "Something went wrong" for a refused field and a lost connection left
// people guessing (CMS review round 2, georges-03).
const FALLBACK_ERROR = "Something went wrong on the server. Nothing changed. Try again; if it keeps happening, tell the team.";
const OFFLINE_ERROR = "No internet connection. Nothing changed: what you typed is still here. Try again when you’re back online.";
const UNREACHABLE_ERROR = "We could not reach the server. Nothing changed. Check your connection and try again.";
// Fastify's own refusal of a field ("body/group_id must be number"): the
// field, in words ("group").
const REFUSED_FIELD = /^body\/([a-z_]+)\b/i;
const fieldWords = (name) => name.replace(/_id$/i, "").replace(/_/g, " ").trim();
const TECHNICAL =
  /status code|stack|\bat [\w.$<>]+ \(|sql|ER_[A-Z_]+|sequelize|syntax ?error|TypeError|ReferenceError|undefined|null|ECONN|ETIMEDOUT|errno|<\/?html|exception|internal server error|network error|timeout of \d+ms/i;
// Database messages the API passes through as they are ("Data truncated for
// column 'type' at row 1"): they mean nothing to the person reading them.
const DATABASE =
  /data truncated|incorrect [\w ]+ value|duplicate entry|unknown column|cannot (add|update|delete) (or update )?a (parent|child) row|foreign key constraint|column '[^']*' cannot be|doesn'?t have a default value|out of range value|table '[^']*' doesn'?t exist|deadlock found|lock wait timeout|too many connections|at row \d+/i;
const isTechnical = (text) => TECHNICAL.test(text) || DATABASE.test(text);

export function friendlyErrorMessage(err) {
  if (!err?.response) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return OFFLINE_ERROR;
    return UNREACHABLE_ERROR;
  }
  const status = err.response.status;
  const message = err.response.data?.message;
  // A server fault (5xx) is never explained by its own text: it can be
  // anything the server or a proxy wrote. Only a 4xx sentence (a rule the
  // person can act on) is passed through.
  const serverFault = !(status >= 400 && status < 500);
  if (!serverFault && typeof message === "string" && message.trim() && message.length <= 200 && !isTechnical(message) && !REFUSED_FIELD.test(message.trim())) {
    const text = message.trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
  // Kept for whoever debugs it, not shown in the toast.
  if (typeof message === "string" && message.trim()) console.warn(`API error ${status ?? ""}: ${message}`);
  if (status === 401) return "Your session has ended. Please sign in again.";
  if (status === 403) return "You do not have access to do this. Ask an admin if you need it.";
  if (status === 404) return "We could not find that. It may have been deleted.";
  if (status === 413) return "That file is too large. Try a smaller one.";
  if (!serverFault) {
    const field = typeof message === "string" && message.match(REFUSED_FIELD);
    return field
      ? `The server didn’t accept “${fieldWords(field[1])}”. Nothing changed. Check it and try again.`
      : "The server didn’t accept what was sent. Nothing changed. Check the form and try again.";
  }
  return FALLBACK_ERROR;
}

// A success toast only says something real; an empty server message becomes
// a plain "Done." A caller that passes its own words (showalert: "Visa type
// added.") gets those instead of the server's.
// The server's stock replies ("Updated successfully") say nothing about
// which item. When the request names one, the toast does: "“Passport”
// saved." (CMS review karim-23, georges-08). Otherwise a short "Saved.".
const STOCK = /^(updated|deleted|created|added|inserted|saved|removed)( successfully)?\.?$|^(success|successful|request successful|done)\.?$/i;
const STOCK_VERB = { updated: "saved", saved: "saved", deleted: "deleted", removed: "removed", created: "added", added: "added", inserted: "added" };
const NAME_FIELDS = ["documents_name", "list_name", "type_name", "group_name", "title", "name", "questions", "fullname"];
function nameIn(data) {
  if (!data) return null;
  const get = (key) => (typeof FormData !== "undefined" && data instanceof FormData ? data.get(key) : data[key]);
  for (const key of NAME_FIELDS) {
    const value = get(key);
    if (typeof value === "string" && value.trim() && value.trim().length <= 80) return value.replace(/<[^>]*>/g, "").trim();
  }
  const person = [get("first_name"), get("last_name")].filter((v) => typeof v === "string" && v.trim()).join(" ");
  return person || null;
}
export function stockMessage(message, data) {
  const text = String(message || "").trim();
  if (!STOCK.test(text)) return null;
  const word = text.toLowerCase().split(/\s+/)[0].replace(/\.$/, "");
  const verb = STOCK_VERB[word] || "saved";
  const name = nameIn(data);
  return name ? `“${name}” ${verb}.` : `${verb.charAt(0).toUpperCase()}${verb.slice(1)}.`;
}

const successMessage = (res, own, data) => {
  if (typeof own === "string" && own.trim()) return own;
  const message = res?.data?.message;
  if (typeof message !== "string" || !message.trim() || isTechnical(message)) return "Done.";
  return stockMessage(message, data) || message.trim();
};

// showerror: true toasts the plain message; false shows nothing (the screen
// says it inline); a string toasts those words instead, for a caller that
// knows what failed ("Your changes weren't saved. Try again.").
const showError = (err, showerror = true) => {
  const own = typeof showerror === "string" && showerror.trim() ? showerror : null;
  const text = own && err?.response?.status !== 401 ? own : friendlyErrorMessage(err);
  if (showerror === false) return text;
  // The same failure from several requests at once shows one toast.
  toast.error(text, { toastId: `api-error-${text}` });
  return text;
};

export async function GetFunction({
  Route,
  data,
  showalert,
  showerror = true,
  state,
  tokens,
  dispatch,
  is_client = false,
}) {
  try {
    const dataUser = getLocalStorage(dataLocalStorage.userinfo);

    let token;
    if (dataUser || tokens) {
      if (tokens) {
        token = tokens;
      } else {
        token = dataUser?.token ?? "";
      }
    }
    // printData({
    //   name: "This data send to " + Route + " :===>",
    //   data,
    //   type: "api",
    // });
    const res = is_client
      ? await axios_client.get(
          Route,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
          { params: data }
        )
      : await axios.get(
          Route,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
          { params: data }
        );
    if (res) {
      if (showalert) {
        toast.success(successMessage(res, showalert, data));
      }
      // printData({
      //   name: "This response from " + Route + " <===:",
      //   data: res?.data,
      //   type: "api",
      // });

      if (res?.data) {
        return res.data;
      } else if (res) {
        return res;
      } else {
        return true;
      }
    } else {
      return false;
    }
  } catch (err) {
    const text = showError(err, showerror);
    dispatch?.(setLoading(false));
    if (err?.response?.status == 401) {
      dispatch?.(SessionEnded());
    }
    throw new Error(text);
  }
}

// showerror: false when the caller says what went wrong itself (inline, or in
// one toast of its own), or a string: the toast in the caller's words.
export async function PostFunction({
  Route,
  data,
  showalert,
  showerror = true,
  isMultipart = true,
  state,
  dispatch,
  is_client = false,
}) {
  try {
    const dataUser = await getLocalStorage(dataLocalStorage.userinfo);
    let token;
    if (dataUser) {
      token = dataUser?.token ?? "";
    }
    // printData({
    //   name: "This data send to " + Route + " :===>",
    //   data,
    //   type: "api",
    // });

    let header;
    if (isMultipart) {
      header = {
        method: "post",
        url: Route,
        data: data,
        headers: {
          "Content-Type": "multipart/form-data",
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
    } else {
      header = {
        method: "post",
        url: Route,
        data: data,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    }
    const res = is_client ? await axios_client(header) : await axios(header);
    if (res) {
      if (showalert) {
        // A delete the Activity log can restore carries Undo (10 seconds).
        const undo = undoFor(Route, data);
        // Undo waits for slow readers: 20 s, paused while hovered or out of focus (georges-09).
        if (undo && canUndo()) toast.success(createElement(UndoToast, { message: successMessage(res, showalert, data), undo }), { autoClose: 20000, closeOnClick: false, pauseOnFocusLoss: true });
        else toast.success(successMessage(res, showalert, data));
      }
      // printData({
      //   name: "This response from " + Route + " <===:",
      //   data: res?.data,
      //   type: "api",
      // });

      if (res?.data) {
        return res.data;
      } else if (res) {
        return res;
      } else {
        return true;
      }
    } else {
      return false;
    }
  } catch (err) {
    const text = showError(err, showerror);
    dispatch?.(setLoading(false));
    if (err?.response?.status == 401) {
      dispatch?.(SessionEnded());
    }
    // The status rides along, for callers that say something of their own
    // for one (503: "not set up on this server yet").
    const error = new Error(text);
    error.status = err?.response?.status ?? null;
    throw error;
  }
}
